import React from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Loader2 } from 'lucide-react';
import { Unit, UnitCreate, UnitUpdate } from '@/app/types/unit';

// Zod Schema for Unit Form
const formSchema = z.object({
  name: z.string().min(1, 'Unit name is required').max(50),
  code: z.string().min(1, 'Unit code is required').max(20)
    .refine(s => !/\s/.test(s), 'Code cannot contain spaces'),
  description: z.string().optional().nullable(),
  is_base: z.boolean(),
  is_active: z.boolean(),
});

type UnitFormValues = z.infer<typeof formSchema>;

interface UnitFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: UnitCreate | UnitUpdate) => void;
  defaultValues?: Partial<Unit>;
  isLoading?: boolean;
  mode: 'create' | 'edit';
}

const UnitForm: React.FC<UnitFormProps> = ({
  open,
  onOpenChange,
  onSubmit,
  defaultValues,
  isLoading = false,
  mode,
}) => {
  const form = useForm<UnitFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: defaultValues?.name || '',
      code: defaultValues?.code || '',
      description: defaultValues?.description || '',
      is_base: defaultValues?.is_base ?? false,
      is_active: defaultValues?.is_active ?? true,
    },
  });

  // Disable code editing in edit mode
  const isCodeDisabled = mode === 'edit';

  const handleSubmit: SubmitHandler<UnitFormValues> = (values) => {
    const dataToSend = mode === 'edit'
      ? { 
          name: values.name, 
          description: values.description, 
          is_base: values.is_base,
          is_active: values.is_active 
        }
      : values;
    onSubmit(dataToSend);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'Add New Unit' : 'Edit Unit'}</DialogTitle>
          <DialogDescription>
            {mode === 'create' ? 'Define a new measurement unit.' : 'Update the unit details.'}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name*</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="e.g., Kilogram" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Code*</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="e.g., KG, NOS" disabled={isCodeDisabled} />
                  </FormControl>
                  {isCodeDisabled && (
                    <FormDescription>Code cannot be changed after creation.</FormDescription>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder="Enter description"
                      value={field.value || ''}
                      rows={3}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="is_base"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox 
                      checked={field.value} 
                      onCheckedChange={field.onChange} 
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel className="font-normal">Base Unit</FormLabel>
                    <FormDescription>
                      Base units are the primary units of measurement in their category.
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox 
                      checked={field.value} 
                      onCheckedChange={field.onChange} 
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel className="font-normal">Active</FormLabel>
                    <FormDescription>
                      Inactive units won&apos;t be available for selection in forms.
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {isLoading 
                  ? (mode === 'create' ? 'Creating...' : 'Updating...') 
                  : (mode === 'create' ? 'Create Unit' : 'Save Changes')
                }
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default UnitForm; 